import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  amountFor,
  isIncluded,
  applyRows,
  money,
  parseAmount,
  paymentIssue,
  previewRows,
  totalsFor,
} from "./payrollModel";
import {
  downloadCSV,
  FIELDS,
  mapRows,
  readSpreadsheet,
  suggestMapping,
} from "./payrollImport";
import {
  downloadPayrollPDF,
  monthlyPayrolls,
  payrollStatus,
} from "./payrollPDF";
import "./PayrollWorkspace.css";

const Icon = ({ name }) => (
  <i aria-hidden="true" className={`fa-solid fa-${name}`} />
);
const periodLabel = (period) =>
  new Date(`${period}-01T12:00:00`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
const initials = (name) =>
  name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");
function Modal({ title, children, close, wide = false }) {
  const ref = useRef();
  useEffect(() => {
    const previous = document.activeElement;
    ref.current?.querySelector("button, input, select")?.focus();
    const handle = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "Tab") {
        const items = [
          ...ref.current.querySelectorAll(
            "button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href]",
          ),
        ];
        const first = items[0],
          last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    const dialog = ref.current;
    dialog.addEventListener("keydown", handle);
    return () => {
      dialog.removeEventListener("keydown", handle);
      previous?.focus();
    };
  }, [close, title]);
  return (
    <div className="pr-overlay">
      <section
        className={`pr-dialog ${wide ? "pr-dialog-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pr-dialog-title"
        ref={ref}
      >
        <header>
          <h2 id="pr-dialog-title">{title}</h2>
          <button className="pr-icon" aria-label="Close dialog" onClick={close}>
            <Icon name="xmark" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
const Field = ({ label, children }) => (
  <label className="pr-field">
    <span>{label}</span>
    {children}
  </label>
);
const Totals = ({ rows }) => (
  <>
    {Object.entries(totalsFor(rows)).map(([currency, amount]) => (
      <div className="pr-total" key={currency}>
        <span>{currency}</span>
        <strong>
          {rows.some((row) => row.currency === currency && row.amount != null)
            ? money(amount, currency)
            : `${currency} · Not set`}
        </strong>
      </div>
    ))}
  </>
);
export default function PayrollWorkspace({ storageKey, company }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emptyState = () => {
    const date = new Date();
    const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      version: 2,
      started: company.draft?.started || false,
      companyId: company.companyId,
      period,
      paymentDate: `${period}-28`,
      bank: company.bank || null,
      runs: company.runs || [],
      overrides: {},
      excluded: [],
      ...company.draft,
      employees: company.employees,
      hires: company.hires,
    };
  };
  // Server is the source of truth. Sensitive fields are never stored in Web Storage.
  const [state, setState] = useState(emptyState);
  useEffect(() => {
    setState((previous) => ({
      ...previous,
      employees: company.employees,
      hires: company.hires,
    }));
  }, [company.employees, company.hires]);
  const [tab, setTab] = useState("payroll");
  const [modal, setModal] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sheet, setSheet] = useState(null);
  const [mapping, setMapping] = useState({});
  const [rows, setRows] = useState([]);
  const [recurring, setRecurring] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState([]);
  const [activity, setActivity] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [confirmationPassword, setConfirmationPassword] = useState("");
  const submissionKey = useRef(null);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    const warn = (event) => {
      if (dirty || busy) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, busy]);
  const fileRef = useRef();
  const openedEmployee = useRef("");
  useEffect(() => {
    const employeeId = searchParams.get("employee");
    const employee = state.employees.find((e) => e.id === employeeId);
    if (employee && openedEmployee.current !== employeeId) {
      openedEmployee.current = employeeId;
      setEdit({
        ...employee,
        amount: String((amountFor(state, employee) || 0) / 100 || ""),
        repeat: false,
      });
      setModal("employee");
    }
  }, [searchParams, state]);
  useEffect(() => {
    // Remove drafts written by the earlier prototype; never deserialize them.
    try {
      const obsoleteKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key === storageKey ||
          key?.startsWith("jobseeqr-payroll-preview-v2:")
        )
          obsoleteKeys.push(key);
      }
      obsoleteKeys.forEach((key) => sessionStorage.removeItem(key));
    } catch (_) {
      setNotice(
        "Old payroll browser drafts could not be cleared. Clear this site's browser storage before using a shared device.",
      );
    }
  }, [storageKey]);
  const close = React.useCallback(() => {
    setConfirmationPassword("");
    setModal(null);
    setError("");
  }, []);
  const open = (type) => {
    setError("");
    setNotice("");
    setModal(type);
  };
  const currentRun = state.runs.find((r) => r.period === state.period);
  const started = state.started || state.runs.length > 0;
  const eligible = state.employees.filter(
    (e) => e.active && isIncluded(state, e) && !state.excluded?.includes(e.id),
  );
  const issues = eligible.filter((e) => paymentIssue(state, e));
  const selectionState = {
    ...state,
    excluded: [],
    includedIds: selection,
    employees: state.employees.map((e) => ({
      ...e,
      enrolled: selection.includes(e.id),
    })),
  };
  const duplicateSelection = selectionState.employees.some(
    (e) =>
      e.enrolled &&
      e.active &&
      paymentIssue(selectionState, e) === "Duplicate employee selected",
  );
  const draftRows = eligible.map((e) => ({
    ...e,
    amount: amountFor(state, e),
  }));
  const acceptWorkspace = (next, saved) => {
    const updated = {
      ...next,
      ...saved.draft,
      employees: saved.employees,
      hires: saved.hires,
      bank: saved.bank,
      runs: saved.runs,
    };
    setState(updated);
    setDirty(false);
    return updated;
  };
  const commit = async (next) =>
    acceptWorkspace(next, await company.saveWorkspace(next));
  const change = async (next, message = "Payroll saved.") => {
    if (busy) return;
    setBusy(true);
    setError("");
    setState(next);
    setDirty(true);
    try {
      await commit(next);
      setNotice(message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const exportMonth = async (month) => {
    if (busy) return;
    setBusy(true);
    try {
      if (dirty && month.period === state.period && !currentRun)
        await commit(state);
      const report = await company.downloadMonth(month.period);
      downloadPayrollPDF(report);
      setNotice(
        "Breakdown downloaded. This file contains salary information; store and share it securely.",
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const showActivity = async () => {
    setBusy(true);
    try {
      setActivity((await company.activity()).events);
      open("activity");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const stageSheet = (data) => {
    setSheet(data);
    setMapping(suggestMapping(data.headers));
    setRows([]);
    setRecurring(true);
    open("import");
  };
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      stageSheet(await readSpreadsheet(file));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };
  const openEdit = (employee) => {
    setEdit({
      ...employee,
      enrolled: isIncluded(state, employee),
      amount: String((amountFor(state, employee) || 0) / 100 || ""),
      repeat: false,
    });
    open("employee");
  };
  const startManual = () => {
    setEdit({
      name: "",
      email: "",
      employeeId: "",
      amount: "",
      currency: "USD",
      account: "",
      active: true,
      enrolled: true,
    });
    open("manual");
  };
  const openedHire = useRef("");
  useEffect(() => {
    const requested = searchParams.get("hire");
    const hire = state.hires.find((h) => h.id === requested);
    if (hire && openedHire.current !== requested) {
      openedHire.current = requested;
      setRows(previewRows([{ ...hire, hireId: hire.id, amount: null }], state));
      setModal("matches");
    }
  }, [searchParams, state]);
  const confirmRows = async () => {
    setBusy(true);
    try {
      const updated = applyRows(state, rows, recurring);
      updated.started = true;
      await commit(updated);
      close();
      setTab("payroll");
      setNotice(
        "Employees matched and payroll saved. New people are now available in My Team.",
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const saveEmployee = async (event) => {
    event.preventDefault();
    try {
      const amount = parseAmount(edit.amount);
      if (modal === "manual") {
        setRows(previewRows([{ ...edit, amount }], state));
        setRecurring(true);
        open("matches");
        return;
      }
      const updated = {
        ...state,
        started: true,
        includedIds: edit.enrolled
          ? [
              ...new Set([
                ...(state.includedIds ||
                  state.employees.filter((e) => e.enrolled).map((e) => e.id)),
                edit.id,
              ]),
            ]
          : (
              state.includedIds ||
              state.employees.filter((e) => e.enrolled).map((e) => e.id)
            ).filter((id) => id !== edit.id),
        overrides: { ...state.overrides },
        employees: state.employees.map((e) =>
          e.id === edit.id
            ? {
                ...e,
                account: edit.account.trim(),
                currency: edit.currency,
                active: edit.active,
                enrolled: edit.enrolled,
                recurring:
                  edit.repeat || edit.currency !== e.currency
                    ? amount
                    : e.recurring,
              }
            : e,
        ),
      };
      if (amount != null) updated.overrides[edit.id] = amount;
      else {
        updated.overrides[edit.id] = 0;
        if (edit.repeat)
          updated.employees.find((e) => e.id === edit.id).recurring = null;
      }
      setBusy(true);
      await commit(updated);
      close();
      setNotice("Payment details saved.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    if (busy || !company.canSubmit) return;
    setBusy(true);
    setError("");
    try {
      if (dirty) await commit(state);
      if (
        !submissionKey.current ||
        submissionKey.current.period !== state.period
      ) {
        submissionKey.current = {
          period: state.period,
          key: crypto.randomUUID(),
        };
      }
      const saved = await company.submit(
        state.period,
        submissionKey.current.key,
      );
      acceptWorkspace(state, saved);
      close();
      setNotice(
        "Submission recorded. Check the bank status below for confirmation.",
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const refreshBank = async (run) => {
    setBusy(true);
    try {
      acceptWorkspace(state, await company.refreshStatus(run.id));
      setNotice("Bank status checked.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const nextMonth = () => {
    const date = new Date(`${state.period}-01T12:00:00`);
    date.setMonth(date.getMonth() + 1);
    const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    change(
      {
        ...state,
        started: true,
        period,
        paymentDate: `${period}-28`,
        overrides: {},
        excluded: [],
      },
      "Next month prepared from recurring amounts. One-time adjustments cleared.",
    );
    setTab("payroll");
    setNotice(
      "Next month prepared from recurring amounts. One-time adjustments cleared.",
    );
  };
  const renderMatches = () => (
    <>
      <p className="pr-muted">
        One person, one employee record. Confirm how each row joins your team.
      </p>
      <div className="pr-match-list">
        {rows.map((row, index) => (
          <article
            className={`pr-match ${!row.choice ? "pr-needs-review" : ""}`}
            key={row.key}
          >
            <div className="pr-person">
              <span className="pr-avatar">{initials(row.name)}</span>
              <div>
                <strong>{row.name}</strong>
                <small>{row.email || row.employeeId}</small>
              </div>
            </div>
            <div className="pr-match-arrow">
              <Icon name="arrow-right" />
              <small>
                {row.duplicate
                  ? "Repeated identity in file · skipped"
                  : row.match.reason}
              </small>
            </div>
            <Field label={`Destination for ${row.name}`}>
              <select
                value={row.choice}
                onChange={(e) =>
                  setRows(
                    rows.map((r, i) =>
                      i === index ? { ...r, choice: e.target.value } : r,
                    ),
                  )
                }
              >
                <option value="">Choose a match…</option>
                <optgroup label="Existing employees">
                  {state.employees.map((e) => (
                    <option key={e.id} value={`employee:${e.id}`}>
                      {e.name} · {e.employeeId || e.email}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="JOBSEEQR hires">
                  {state.hires
                    .filter((h) => !h.employeeKey)
                    .map((h) => (
                      <option key={h.id} value={`hire:${h.id}`}>
                        Link hire: {h.name}
                      </option>
                    ))}
                </optgroup>
                <option value="new:">Create new employee</option>
                <option value="skip">Skip this row</option>
              </select>
            </Field>
            {row.amount != null && (
              <small className="pr-import-amount">
                Payment:{" "}
                {money(
                  row.amount,
                  row.currency ||
                    state.employees.find(
                      (e) => `employee:${e.id}` === row.choice,
                    )?.currency ||
                    "USD",
                )}
              </small>
            )}
          </article>
        ))}
      </div>
      {rows.some((row) => row.amount != null) && (
        <>
          <label className="pr-check">
            <input
              type="checkbox"
              checked={recurring}
              onChange={(e) => setRecurring(e.target.checked)}
            />
            Save imported amounts as recurring monthly payments
          </label>
          <p className="pr-muted pr-small">
            Unchecked: amounts apply to this month only. Employees absent from
            this import stay unchanged. Existing identity fields are preserved
            when linking.
          </p>
        </>
      )}
      <footer>
        <button onClick={close}>Cancel</button>
        <button
          className="pr-primary"
          disabled={
            rows.some((r) => !r.choice) ||
            rows.every((r) => r.choice === "skip")
          }
          onClick={confirmRows}
        >
          Confirm {rows.filter((r) => r.choice !== "skip").length}{" "}
          {rows.length === 1 ? "person" : "people"} <Icon name="arrow-right" />
        </button>
      </footer>
    </>
  );
  const renderResults = (run) =>
    run.purged ? (
      <section className="pr-run-results">
        <h2>{periodLabel(run.period)}</h2>
        <p>
          The employee breakdown was removed under your retention policy. The
          submission reference is retained to prevent duplicate delivery.
        </p>
        <button className="pr-primary" onClick={nextMonth}>
          Prepare next month
        </button>
      </section>
    ) : (
      <section className="pr-run-results">
        <div className="pr-section-heading">
          <div>
            <span className="pr-eyebrow">BANK HANDOFF</span>
            <h2>{periodLabel(run.period)}</h2>
            <p className="pr-muted">
              Confirmed by employer {new Date(run.approvedAt).toLocaleString()}{" "}
              · {run.bank.name} · {run.bank.account}
            </p>
          </div>
          <button disabled={busy} onClick={() => exportMonth(run)}>
            <Icon name="download" /> Download breakdown PDF
          </button>
        </div>
        <div className="pr-handoff-status">
          <Icon
            name={run.status === "paid" ? "circle-check" : "building-columns"}
          />
          <div>
            <h3>{payrollStatus(run)}</h3>
            <p>
              {["unknown", "sending"].includes(run.status)
                ? "The bank has not confirmed receipt. Check status before taking any further action. This payroll will not be sent again automatically."
                : run.status === "rejected"
                  ? "The bank rejected this submission. Contact your bank before arranging a corrected submission."
                  : run.status === "paid"
                    ? "The bank confirmed the payments."
                    : "Your bank handles authorization and payment. Delivery alone does not confirm that employees have been paid."}
            </p>
            {run.bankReference && (
              <small>Bank reference: {run.bankReference}</small>
            )}
            <small>JOBSEEQR reference: {run.id}</small>
          </div>
        </div>
        <Totals rows={run.entries} />
        <div className="pr-actions">
          <button disabled={busy} onClick={() => refreshBank(run)}>
            <Icon name="rotate" /> Check bank status
          </button>
          <button disabled={busy} className="pr-primary" onClick={nextMonth}>
            Prepare next month <Icon name="arrow-right" />
          </button>
        </div>
      </section>
    );

  return (
    <div className="pr-workspace">
      <header className="team-toolbar pr-header">
        <h2 className="manage-title">Payroll</h2>
        {started && (
          <div className="pr-actions">
            <span className="pr-preview-label" role="status">
              {busy ? "Saving…" : dirty ? "Unsaved changes" : "Saved"}
            </span>
            {dirty && (
              <button disabled={busy} onClick={() => change(state)}>
                Save draft
              </button>
            )}
            <button disabled={busy} onClick={showActivity}>
              <Icon name="clock-rotate-left" /> Activity
            </button>
            <button
              className="post-job-cta secondary"
              onClick={() => navigate("/my-team")}
            >
              <Icon name="users" />
              My Team
            </button>
            <button
              disabled={busy}
              className="post-job-cta"
              onClick={() => open("bank")}
            >
              <Icon name="building-columns" />
              {state.bank ? state.bank.name : "Set up bank"}
            </button>
          </div>
        )}
      </header>
      <p className="pr-muted pr-small" role="note">
        Salary and bank details are restricted to payroll users. Your bank
        authorizes and executes payments.
      </p>
      {company.legacyPaymentData && (
        <p className="pr-error" role="alert">
          Older payroll details remain in employee records. Ask your
          administrator to migrate them into protected payroll storage before
          using real payment data.
        </p>
      )}
      {started && (
        <nav className="team-controls pr-tabs" aria-label="Payroll workspace">
          {[
            ["payroll", "This payroll"],
            ["payments", "Monthly payments"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`chip ${tab === key ? "chip-active" : ""}`}
              aria-current={tab === key ? "page" : undefined}
              onClick={() => {
                setTab(key);
                setError("");
              }}
            >
              {label}
            </button>
          ))}
        </nav>
      )}
      {notice && (
        <div className="pr-notice" role="status">
          <Icon name="check" />
          {notice}
        </div>
      )}
      {error && !modal && (
        <div className="pr-error" role="alert">
          {error}
        </div>
      )}
      <input
        type="file"
        ref={fileRef}
        accept=".csv,.xlsx"
        hidden
        onChange={upload}
      />
      {!started && (
        <section className="pr-source-choice">
          <div className="pr-source-intro">
            <h3>Start with your people.</h3>
            <p>Choose your team or upload your salary spreadsheet.</p>
          </div>
          <div className="pr-source-buttons">
            <button
              disabled={busy}
              onClick={() =>
                change({
                  ...state,
                  started: true,
                  includedIds: state.employees
                    .filter((e) => e.active)
                    .map((e) => e.id),
                })
              }
            >
              <span className="pr-source-icon">
                <Icon name="users" />
              </span>
              <strong>My Team</strong>
              <span>Use employees already on JOBSEEQR</span>
              <Icon name="arrow-right" />
            </button>
            <button onClick={() => open("upload")}>
              <span className="pr-source-icon">
                <Icon name="file-arrow-up" />
              </span>
              <strong>Import spreadsheet</strong>
              <span>Upload an Excel or CSV salary sheet</span>
              <Icon name="arrow-right" />
            </button>
          </div>
          <p className="pr-source-footnote">
            Match existing people and add new employees to My Team, without
            duplicate entries.
          </p>
        </section>
      )}
      {started && tab === "payroll" && !currentRun && (
        <fieldset
          disabled={busy}
          className="pr-payroll-layout pr-workspace-fields"
        >
          <section className="pr-register">
            <div className="pr-section-heading">
              <div>
                <span className="pr-eyebrow">MONTHLY PAYROLL / DRAFT</span>
                <h2>{periodLabel(state.period)}</h2>
                <p className="pr-muted">
                  {eligible.length}{" "}
                  {eligible.length === 1 ? "person" : "people"} ·{" "}
                  {issues.length
                    ? `${issues.length} need your attention`
                    : eligible.length
                      ? "Everyone’s payment details are ready"
                      : "Include employees or import your salary sheet"}
                </p>
              </div>
              <button onClick={() => open("upload")}>
                <Icon name="file-arrow-up" /> Import salaries
              </button>
            </div>
            <div className="pr-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Amount to pay</th>
                    <th>Readiness</th>
                    <th>
                      <span className="pr-sr">Include employee</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!eligible.length && (
                    <tr>
                      <td colSpan={4} className="pr-empty">
                        No employees included yet. Import a spreadsheet or
                        choose people from your directory.
                      </td>
                    </tr>
                  )}
                  {eligible.map((e) => (
                    <tr
                      key={e.id}
                      className={
                        paymentIssue(state, e) ? "pr-row-attention" : ""
                      }
                    >
                      <td>
                        <div className="pr-person">
                          <span className="pr-avatar">{initials(e.name)}</span>
                          <div>
                            <button
                              className="pr-text-button"
                              onClick={() => openEdit(e)}
                            >
                              {e.name}
                            </button>
                            <small>
                              {e.role || e.employeeId || "Employee"}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="pr-numeric">
                        <button
                          className="pr-amount-button"
                          onClick={() => openEdit(e)}
                        >
                          {amountFor(state, e)
                            ? money(amountFor(state, e), e.currency)
                            : "Add amount"}{" "}
                          <Icon name="pen" />
                        </button>
                        <small>
                          {state.overrides[e.id] != null &&
                          state.overrides[e.id] !== e.recurring
                            ? "This month only"
                            : "Recurring monthly"}
                        </small>
                      </td>
                      <td>
                        {paymentIssue(state, e) ? (
                          <button
                            className="pr-warning-link"
                            onClick={() => openEdit(e)}
                          >
                            <Icon name="circle-exclamation" />{" "}
                            {paymentIssue(state, e)}
                          </button>
                        ) : (
                          <span className="pr-ready">
                            <Icon name="check" /> Ready
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="pr-icon"
                          aria-label={`Exclude ${e.name} from this payroll`}
                          onClick={() =>
                            change({
                              ...state,
                              excluded: [...(state.excluded || []), e.id],
                            })
                          }
                        >
                          <Icon name="minus" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="pr-add-row"
              onClick={() => {
                setSelection(eligible.map((e) => e.id));
                open("team");
              }}
            >
              <Icon name="plus" /> Include employees from the directory
            </button>
            <button
              disabled={busy || !company.canCreateEmployees}
              className="pr-add-row"
              onClick={startManual}
            >
              <Icon name="plus" /> Add an employee manually
            </button>
            <p className="pr-muted pr-small">
              Enter final amounts approved by your employer or accountant.
            </p>
          </section>
          <aside className="pr-pay-summary">
            <span className="pr-eyebrow">PAYMENT SUMMARY</span>
            <Totals rows={draftRows} />
            <div className="pr-summary-divider" />
            <Field label="Payment date">
              <input
                type="date"
                value={state.paymentDate}
                min={`${state.period}-01`}
                max={`${state.period}-${new Date(Number(state.period.slice(0, 4)), Number(state.period.slice(5)), 0).getDate()}`}
                onChange={(e) => {
                  setState({ ...state, paymentDate: e.target.value });
                  setDirty(true);
                }}
                onBlur={() => {
                  if (dirty) change(state);
                }}
              />
            </Field>
            <p className="pr-small pr-muted">
              Bank fees are additional and depend on the bank agreement.
            </p>
            <div
              className={`pr-summary-readiness ${issues.length ? "pr-warning" : ""}`}
            >
              <Icon name={issues.length ? "circle-exclamation" : "check"} />
              <span>
                {issues.length
                  ? `${issues.length} payment${issues.length > 1 ? "s need" : " needs"} details`
                  : eligible.length
                    ? "Ready for your review"
                    : "Include employees to continue"}
              </span>
            </div>
            {!state.bank && (
              <button className="pr-bank-inline" onClick={() => open("bank")}>
                Set up paying account <Icon name="arrow-right" />
              </button>
            )}
            <button
              className="pr-primary"
              disabled={
                busy ||
                !company.canSubmit ||
                !eligible.length ||
                issues.length > 0 ||
                !state.bank ||
                !state.paymentDate
              }
              onClick={() => open("review")}
            >
              Review payroll <Icon name="arrow-right" />
            </button>
            <small>
              {company.canSubmit
                ? "Review first. Nothing is submitted yet."
                : "A payroll approver can submit this to the bank."}
            </small>
          </aside>
        </fieldset>
      )}
      {tab === "payroll" && currentRun && renderResults(currentRun)}
      {started && tab === "payments" && (
        <section className="pr-months">
          <div className="pr-section-heading">
            <div>
              <h2>Monthly payments</h2>
              <p className="pr-muted">
                One record per payroll month. Download the employee breakdown
                when you need it.
              </p>
            </div>
          </div>
          <div className="pr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Employees</th>
                  <th>Total payroll</th>
                  <th>Status</th>
                  <th>
                    <span className="pr-sr">Download</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthlyPayrolls(state).map((month) => (
                  <tr key={month.period}>
                    <td>
                      <strong>{periodLabel(month.period)}</strong>
                      <small>
                        {month.draft
                          ? "Current payroll"
                          : `Payment date: ${month.date}`}
                      </small>
                    </td>
                    <td>{month.entries.length}</td>
                    <td>
                      <Totals rows={month.entries} />
                      {!month.entries.length && "—"}
                    </td>
                    <td>
                      <span className="pr-month-status">
                        {payrollStatus(month)}
                      </span>
                      {month.entries.some((e) => e.amount == null) && (
                        <small>Some amounts are missing</small>
                      )}
                    </td>
                    <td>
                      <button
                        aria-label={`Download ${periodLabel(month.period)} breakdown PDF`}
                        disabled={busy || month.purged}
                        onClick={() => exportMonth(month)}
                      >
                        <Icon name="file-pdf" /> Download PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pr-muted pr-small">
            Each month keeps its submitted amounts. Downloaded PDFs contain
            salary information and remain on your device.
          </p>
        </section>
      )}
      {modal && (
        <Modal
          title={
            {
              upload: "Bring your spreadsheet",
              import: "Match your columns",
              matches: "Connect people, without duplicates",
              team: "Include your existing employees",
              manual: "Add an employee",
              employee: edit?.name || "Employee",
              bank: "Your paying account",
              review: "Review before submitting",
              activity: "Payroll activity",
            }[modal]
          }
          close={busy ? () => {} : close}
          wide={["matches", "import"].includes(modal)}
        >
          {error && (
            <div className="pr-error" role="alert">
              {error}
            </div>
          )}
          {company.reauthRequired && (
            <form
              className="pr-confirm-identity"
              onSubmit={async (event) => {
                event.preventDefault();
                setBusy(true);
                try {
                  await company.reauthenticate(confirmationPassword);
                  setConfirmationPassword("");
                  setError("");
                  setNotice(
                    "Identity confirmed. You can continue with your bank action.",
                  );
                } catch (_) {
                  setError(
                    "Identity could not be confirmed. Check your sign-in and try again.",
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              <p>Confirm your identity to continue.</p>
              {company.passwordAuth && (
                <Field label="Your JOBSEEQR password">
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={confirmationPassword}
                    onChange={(event) =>
                      setConfirmationPassword(event.target.value)
                    }
                    required
                  />
                </Field>
              )}
              <button className="pr-primary" disabled={busy}>
                Confirm identity
              </button>
            </form>
          )}
          <fieldset
            className="pr-modal-fields"
            disabled={busy || company.reauthRequired}
          >
            {modal === "upload" && (
              <>
                <p className="pr-muted">
                  Upload names, identifiers and payment details. You’ll review
                  every match before saving your payroll.
                </p>
                <button
                  className="pr-upload-zone"
                  disabled={busy}
                  onClick={() => fileRef.current.click()}
                >
                  <Icon name="file-arrow-up" />
                  <strong>
                    {busy ? "Reading spreadsheet…" : "Choose Excel or CSV"}
                  </strong>
                  <span>.xlsx or .csv · up to 1,000 rows · 5 MB</span>
                </button>
                <div className="pr-actions">
                  <button
                    onClick={() =>
                      downloadCSV("payroll-template.csv", [
                        Object.fromEntries(
                          Object.values(FIELDS).map((label) => [label, ""]),
                        ),
                      ])
                    }
                  >
                    <Icon name="download" /> Download template
                  </button>
                </div>
                <p className="pr-muted pr-small">
                  New employees are added to My Team. Existing people are linked
                  by their identifiers. Excel imports read the first sheet;
                  paste formulas as values.
                </p>
              </>
            )}
            {modal === "import" && (
              <>
                <p className="pr-muted">
                  {sheet.filename} · {sheet.rows.length} rows. We’ve suggested
                  the columns we recognize.
                </p>
                <div className="pr-mapping">
                  {Object.entries(FIELDS).map(([key, label]) => (
                    <Field label={label} key={key}>
                      <select
                        value={mapping[key] || ""}
                        onChange={(e) =>
                          setMapping({ ...mapping, [key]: e.target.value })
                        }
                      >
                        <option value="">Not in this file</option>
                        {sheet.headers.map((h) => (
                          <option key={h}>{h}</option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
                <p className="pr-small pr-muted">
                  Names identify the row; employee IDs and emails help identify
                  the person. Missing payment details can be completed later.
                </p>
                <footer>
                  <button onClick={() => open("upload")}>Back</button>
                  <button
                    className="pr-primary"
                    onClick={() => {
                      try {
                        setRows(previewRows(mapRows(sheet, mapping), state));
                        open("matches");
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                  >
                    Review people <Icon name="arrow-right" />
                  </button>
                </footer>
              </>
            )}
            {modal === "matches" && renderMatches()}
            {modal === "team" && (
              <>
                <p className="pr-muted">
                  These are your existing My Team records. Selecting them
                  creates no duplicates.
                </p>
                {duplicateSelection && (
                  <p className="pr-error" role="alert">
                    Selected records share an employee ID or email. Keep one
                    record for each person before continuing.
                  </p>
                )}
                <div className="pr-selection-list">
                  {state.employees
                    .filter((e) => e.active)
                    .map((e) => (
                      <label className="pr-check" key={e.id}>
                        <input
                          type="checkbox"
                          checked={selection.includes(e.id)}
                          onChange={(event) =>
                            setSelection(
                              event.target.checked
                                ? [...selection, e.id]
                                : selection.filter((id) => id !== e.id),
                            )
                          }
                        />
                        <span>
                          <strong>{e.name}</strong>
                          <small>
                            {e.employeeId || e.email} · Record {e.id.slice(-6)}{" "}
                            ·{" "}
                            {e.recurring
                              ? money(e.recurring, e.currency)
                              : "Payment amount needed"}
                          </small>
                        </span>
                      </label>
                    ))}
                </div>
                <footer>
                  <button onClick={close}>Cancel</button>
                  <button
                    className="pr-primary"
                    disabled={duplicateSelection}
                    onClick={() => {
                      if (duplicateSelection) return;
                      change({
                        ...state,
                        started: true,
                        excluded: [],
                        includedIds: selection,
                      });
                      close();
                      setTab("payroll");
                    }}
                  >
                    Include {selection.length} employees
                  </button>
                </footer>
              </>
            )}
            {["manual", "employee"].includes(modal) && (
              <form onSubmit={saveEmployee}>
                {modal === "employee" && (
                  <div className="pr-record-link">
                    <Icon name="link" />
                    <span>
                      {edit.employeeId || edit.id}
                      <small>
                        {edit.hireId
                          ? "Recruitment → Employee → Payroll"
                          : `${edit.source || "My Team"} → Employee → Payroll`}
                      </small>
                    </span>
                  </div>
                )}
                <div className="pr-form-grid">
                  {modal === "manual" && (
                    <>
                      <Field label="Full name">
                        <input
                          required
                          value={edit.name}
                          onChange={(e) =>
                            setEdit({ ...edit, name: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          type="email"
                          value={edit.email}
                          onChange={(e) =>
                            setEdit({ ...edit, email: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Employee ID (optional)">
                        <input
                          value={edit.employeeId}
                          onChange={(e) =>
                            setEdit({ ...edit, employeeId: e.target.value })
                          }
                        />
                      </Field>
                    </>
                  )}
                  <Field label="Final amount to pay">
                    <input
                      inputMode="decimal"
                      value={edit.amount}
                      placeholder="Can be completed later"
                      onChange={(e) =>
                        setEdit({ ...edit, amount: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Currency">
                    <select
                      value={edit.currency}
                      onChange={(e) =>
                        setEdit({ ...edit, currency: e.target.value })
                      }
                    >
                      <option>USD</option>
                      <option>LBP</option>
                    </select>
                  </Field>
                  <Field label="Beneficiary bank account / IBAN">
                    <input
                      value={edit.account}
                      placeholder="Beneficiary account or IBAN"
                      onChange={(e) =>
                        setEdit({ ...edit, account: e.target.value })
                      }
                    />
                  </Field>
                </div>
                {modal === "employee" && (
                  <label className="pr-check">
                    <input
                      type="checkbox"
                      checked={edit.repeat}
                      onChange={(e) =>
                        setEdit({ ...edit, repeat: e.target.checked })
                      }
                    />
                    Use this amount every month
                  </label>
                )}
                <label className="pr-check">
                  <input
                    type="checkbox"
                    checked={edit.enrolled}
                    onChange={(e) =>
                      setEdit({ ...edit, enrolled: e.target.checked })
                    }
                  />
                  Include in payroll
                </label>
                {modal === "employee" && (
                  <p className="pr-small pr-muted">
                    Employment: {edit.status || "active"}.{" "}
                    <button
                      type="button"
                      className="pr-text-button"
                      onClick={() => navigate(`/my-team/employee/${edit.id}`)}
                    >
                      Open full employee record
                    </button>
                  </p>
                )}
                {modal === "employee" &&
                  state.runs.some((r) =>
                    r.entries.some((e) => e.employeeId === edit.id),
                  ) && (
                    <section className="pr-employee-history">
                      <h3>Payment history</h3>
                      {state.runs.flatMap((r) =>
                        r.entries
                          .filter((e) => e.employeeId === edit.id)
                          .map((e) => (
                            <div key={r.id}>
                              <span>
                                {periodLabel(r.period)}
                                <small>
                                  {e.status === "paid"
                                    ? "Paid · bank confirmed"
                                    : e.status}
                                </small>
                              </span>
                              <strong>{money(e.amount, e.currency)}</strong>
                            </div>
                          )),
                      )}
                    </section>
                  )}
                <p className="pr-small pr-muted">
                  Bank-specific validation is applied before submission.
                  Downloaded reports do not include account numbers.
                  {modal === "employee" &&
                    " Changing currency also updates the recurring amount."}
                </p>
                <footer>
                  <button type="button" onClick={close}>
                    Cancel
                  </button>
                  <button className="pr-primary" type="submit">
                    {modal === "manual"
                      ? "Check for existing person"
                      : "Save employee"}
                  </button>
                </footer>
              </form>
            )}
            {modal === "activity" && (
              <div className="pr-activity-list">
                {!activity.length && <p>No payroll activity yet.</p>}
                {activity.map((event) => (
                  <article key={event.id}>
                    <strong>{event.action.replaceAll("_", " ")}</strong>
                    <small>
                      {new Date(event.at).toLocaleString()} · {event.actor}
                    </small>
                    {event.period && <span>{periodLabel(event.period)}</span>}
                  </article>
                ))}
              </div>
            )}
            {modal === "bank" && (
              <>
                {state.bank && (
                  <div className="pr-bank-explainer">
                    <Icon name="building-columns" />
                    <div>
                      <strong>{state.bank.name}</strong>
                      <p>{state.bank.account}</p>
                      <button
                        disabled={!company.canSubmit}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            acceptWorkspace(state, await company.disconnect());
                            close();
                            setNotice(
                              "Bank disconnected for future submissions. Existing submissions remain on record.",
                            );
                          } catch (err) {
                            setError(err.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                )}
                {(company.banks || []).map((bank) => (
                  <form
                    className="pr-bank-option"
                    key={bank.id}
                    onSubmit={async (event) => {
                      event.preventDefault();
                      const code = new FormData(event.currentTarget).get(
                        "authorizationCode",
                      );
                      setBusy(true);
                      try {
                        acceptWorkspace(
                          state,
                          await company.connect(bank.id, code),
                        );
                        close();
                        setNotice("Your bank confirmed the connection.");
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    <h3>
                      <Icon name="building-columns" /> {bank.name}
                    </h3>
                    <span className="pr-month-status">
                      {bank.available ? "Available" : "Awaiting bank setup"}
                    </span>
                    <p className="pr-muted">{bank.message}</p>
                    {bank.available && (
                      <>
                        <Field label="Bank authorization code">
                          <input
                            name="authorizationCode"
                            type="password"
                            autoComplete="off"
                            required
                          />
                        </Field>
                        <button
                          className="pr-primary"
                          disabled={!company.canSubmit}
                        >
                          Connect bank
                        </button>
                      </>
                    )}
                  </form>
                ))}
                <p className="pr-muted pr-small">
                  Only banks with a verified integration can receive payroll.
                  Your online banking password is never requested here.
                </p>
              </>
            )}
            {modal === "review" && (
              <>
                <span className="pr-eyebrow">
                  {periodLabel(state.period)} · {eligible.length} EMPLOYEES
                </span>
                <Totals rows={draftRows} />
                <dl className="pr-review-details">
                  <div>
                    <dt>Paying from</dt>
                    <dd>
                      {state.bank?.name}
                      <small>{state.bank?.account}</small>
                    </dd>
                  </div>
                  <div>
                    <dt>Payment date</dt>
                    <dd>{state.paymentDate}</dd>
                  </div>
                  <div>
                    <dt>Bank fees</dt>
                    <dd>According to bank agreement</dd>
                  </div>
                </dl>
                <div className="pr-review-list">
                  {draftRows.map((e) => (
                    <div key={e.id}>
                      <span>{e.name}</span>
                      <strong>{money(e.amount, e.currency)}</strong>
                    </div>
                  ))}
                </div>
                <p className="pr-small pr-muted">
                  Confirming saves these amounts and sends the completed payroll
                  sheet and notice to your bank. The bank handles its required
                  authorization and payment process.
                </p>
                <footer>
                  <button onClick={close}>Back to payroll</button>
                  <button className="pr-primary" onClick={submit}>
                    Send sheet to bank <Icon name="arrow-right" />
                  </button>
                </footer>
              </>
            )}
          </fieldset>
        </Modal>
      )}
    </div>
  );
}
